import React, { useState, useEffect } from 'react';
import api from '../services/api';

export default function TokenAggregator() {
  const [activeTab, setActiveTab] = useState('tokens');
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Data states
  const [tokens, setTokens] = useState([]);
  const [totalTokens, setTotalTokens] = useState(0);
  const [stats, setStats] = useState(null);
  const [recoveryCandidates, setRecoveryCandidates] = useState([]);
  
  // Filters
  const [chainFilter, setChainFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('addedAt');
  const [sortOrder, setSortOrder] = useState(-1);
  
  // Global settings with multi-chain support
  const [settings, setSettings] = useState({
    chains: {
      solana: { enabled: true, name: 'Solana' },
      base: { enabled: true, name: 'Base' },
      bsc: { enabled: true, name: 'BSC' },
      ethereum: { enabled: true, name: 'Ethereum' },
      arbitrum: { enabled: false, name: 'Arbitrum' },
      polygon: { enabled: false, name: 'Polygon' }
    },
    filters: {
      minMarketCap: 5000,
      maxMarketCap: 500000,
      minChange24h: -99,
      maxChange24h: -15,
      athDropThreshold: -80,
      maxAthDrop: -95
    },
    safetyFilters: {
      minLiquidity: 1000,
      minPairAgeHours: 4,
      maxPairAgeHours: 2160,
      min24hTxns: 50,
      min24hVolume: 1000,
      min24hBuys: 10,
      min24hSells: 5,
      maxBuySellRatio: 20,
      min6hTxns: 10,
      min1hTxns: 2,
    },
    autoAddToTokens: true,
    autoAddToRecovery: true,
    maxTokensPerRefresh: 50,
    enabled: true
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [showSafetyFilters, setShowSafetyFilters] = useState(false);

  const tabs = [
    { id: 'tokens', name: 'Aggregated Tokens', icon: '📊' },
    { id: 'recovery', name: 'Recovery Candidates', icon: '📈' },
    { id: 'settings', name: 'Settings', icon: '⚙️' },
    { id: 'stats', name: 'Statistics', icon: '📉' },
  ];

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, chainFilter, statusFilter, sortBy, sortOrder]);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    
    try {
      if (activeTab === 'tokens') {
        const params = new URLSearchParams();
        if (chainFilter) params.append('chain', chainFilter);
        if (statusFilter) params.append('status', statusFilter);
        params.append('sortBy', sortBy);
        params.append('sortOrder', sortOrder);
        
        const response = await api.get(`/aggregator/tokens?${params.toString()}`);
        setTokens(response.data.tokens || []);
        setTotalTokens(response.data.total || 0);
      } else if (activeTab === 'recovery') {
        const response = await api.get('/aggregator/recovery-candidates');
        setRecoveryCandidates(response.data || []);
      } else if (activeTab === 'settings') {
        const response = await api.get('/aggregator/settings');
        if (response.data) {
          // Ensure chains object exists
          if (!response.data.chains) {
            response.data.chains = {
              solana: { enabled: true, name: 'Solana' },
              base: { enabled: false, name: 'Base' },
              bsc: { enabled: false, name: 'BSC' },
              ethereum: { enabled: false, name: 'Ethereum' },
              arbitrum: { enabled: false, name: 'Arbitrum' },
              polygon: { enabled: false, name: 'Polygon' }
            };
          }
          setSettings(response.data);
        }
      } else if (activeTab === 'stats') {
        const response = await api.get('/aggregator/stats');
        setStats(response.data);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSaveSettings = async () => {
    setSavingSettings(true);
    setError('');
    try {
      await api.put('/aggregator/settings', settings);
      setSuccess('Settings saved successfully!');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };
  
  const summarizeRefresh = (result) => {
    const chains = (result.results || [])
      .map((r) => `${r.name}: ${r.added} new`)
      .join(', ');
    return (
      `Refresh complete! ${result.totalTokensFound} new, ${result.totalAddedToRecovery} to recovery` +
      `${result.staleRemoved ? `, ${result.staleRemoved} stale removed` : ''}.` +
      (chains ? ` (${chains})` : '')
    );
  };

  // A full scan paces external API calls and can run for a few minutes — far longer than the
  // proxy timeout. So we kick off a background job and poll its status until it finishes.
  const handleRefresh = async () => {
    setRefreshing(true);
    setError('');
    setSuccess('');

    try {
      await api.post('/aggregator/refresh');
      setSuccess('Scanning all chains… this can take a couple of minutes.');

      const poll = async () => {
        try {
          const { data } = await api.get('/aggregator/refresh-status');
          if (data.running) {
            setTimeout(poll, 4000);
            return;
          }
          if (data.error) {
            setError(data.error);
          } else if (data.result) {
            setSuccess(summarizeRefresh(data.result));
          } else {
            setSuccess('Refresh complete.');
          }
          setRefreshing(false);
          fetchData();
        } catch (err) {
          setError(err.response?.data?.error || 'Failed to check refresh status');
          setRefreshing(false);
        }
      };

      setTimeout(poll, 4000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start refresh');
      setRefreshing(false);
    }
  };

  const handleAddToRecovery = async (tokenId) => {
    try {
      await api.post(`/aggregator/add-to-recovery/${tokenId}`);
      setSuccess('Token added to recovery tracking!');
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add token to recovery');
    }
  };

  const handleDeleteToken = async (tokenId) => {
    if (!window.confirm('Are you sure you want to remove this token?')) return;
    
    try {
      await api.delete(`/aggregator/tokens/${tokenId}`);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete token');
    }
  };

  const formatPrice = (value) => {
    if (!value && value !== 0) return 'N/A';
    if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(2)}B`;
    if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`;
    if (value >= 1_000) return `$${(value / 1_000).toFixed(2)}K`;
    if (value >= 1) return `$${value.toFixed(2)}`;
    if (value >= 0.0001) return `$${value.toFixed(6)}`;
    return `$${value.toExponential(2)}`;
  };

  const formatPercent = (value) => {
    if (!value && value !== 0) return 'N/A';
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const tokenAddedDate = (token) => token.addedAt || token.updatedAt || token.createdAt;

  const getChainEmoji = (chain) => {
    const emojis = {
      solana: '◎', base: '🔵', ethereum: '⟠', bsc: '🟡',
      arbitrum: '🔷', polygon: '🟣'
    };
    return emojis[chain] || '🔗';
  };

  const CHAIN_FILTER_OPTIONS = [
    { id: 'solana', label: 'Solana' },
    { id: 'base', label: 'Base' },
    { id: 'ethereum', label: 'Ethereum' },
    { id: 'bsc', label: 'BSC' },
    { id: 'arbitrum', label: 'Arbitrum' },
    { id: 'polygon', label: 'Polygon' },
  ];

  const getStatusBadge = (status) => {
    const badges = {
      tracked: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
      recovery_candidate: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200',
      added_to_recovery: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
      recovered: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
    };
    return badges[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Token Aggregator</h1>
            <p className="text-gray-600 dark:text-gray-400">
              Discover tokens across Solana, Base, Ethereum, BSC & more via DEX Screener + GeckoTerminal
            </p>
          </div>
          <button
            onClick={() => handleRefresh()}
            disabled={refreshing}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {refreshing ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Refreshing...
              </>
            ) : (
              <>🔄 Refresh All Sources</>
            )}
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg">
          {error}
          <button onClick={() => setError('')} className="ml-4 text-red-900 font-bold">×</button>
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-100 text-green-700 rounded-lg">
          {success}
          <button onClick={() => setSuccess('')} className="ml-4 text-green-900 font-bold">×</button>
        </div>
      )}

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
        <nav className="flex space-x-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <>
          {/* Tokens Tab */}
          {activeTab === 'tokens' && (
            <div>
              {/* Filters */}
              <div className="mb-4 flex gap-4 flex-wrap">
                <select
                  value={chainFilter}
                  onChange={(e) => setChainFilter(e.target.value)}
                  className="px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700"
                >
                  <option value="">All Chains</option>
                  {CHAIN_FILTER_OPTIONS.map((c) => (
                    <option key={c.id} value={c.id}>{getChainEmoji(c.id)} {c.label}</option>
                  ))}
                </select>
                
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700"
                >
                  <option value="">All Status</option>
                  <option value="tracked">Tracked</option>
                  <option value="recovery_candidate">Recovery Candidate</option>
                  <option value="added_to_recovery">Added to Recovery</option>
                </select>
                
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700"
                >
                  <option value="addedAt">Date Added</option>
                  <option value="athDropPercent">ATH Drop %</option>
                  <option value="currentMarketCap">Market Cap</option>
                  <option value="change24h">24h Change</option>
                </select>
                
                <button
                  onClick={() => setSortOrder(sortOrder === -1 ? 1 : -1)}
                  className="px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700"
                >
                  {sortOrder === -1 ? '↓ Desc' : '↑ Asc'}
                </button>
                
                <span className="px-3 py-2 text-gray-600 dark:text-gray-400">
                  {totalTokens} tokens found
                </span>
              </div>

              {/* Tokens Table */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Chain</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Token</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Current MC</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ATH MC</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Drop from ATH</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">24h Change</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Added</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {tokens.map((token) => (
                      <tr key={token._id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-4 py-3 text-sm capitalize whitespace-nowrap">
                          {getChainEmoji(token.chain)} {token.chain}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center">
                            <div>
                              <div className="font-medium text-gray-900 dark:text-white">
                                {token.symbol}
                              </div>
                              <div className="text-xs text-gray-500 truncate max-w-[150px]">
                                {token.name}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-900 dark:text-white">
                          {formatPrice(token.currentMarketCap)}
                        </td>
                        <td className="px-4 py-3 text-gray-900 dark:text-white">
                          {formatPrice(token.athMarketCap)}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`font-medium ${token.athDropPercent <= -80 ? 'text-red-600' : token.athDropPercent <= -50 ? 'text-orange-600' : 'text-yellow-600'}`}>
                            {formatPercent(token.athDropPercent)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={token.change24h >= 0 ? 'text-green-600' : 'text-red-600'}>
                            {formatPercent(token.change24h)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                          <div>{formatDate(tokenAddedDate(token))}</div>
                          {(() => {
                            const added = tokenAddedDate(token);
                            const age = added
                              ? Math.floor((Date.now() - new Date(added).getTime()) / 86400000)
                              : null;
                            const expiring = age != null && age >= 6 && token.status !== 'added_to_recovery';
                            return (
                              <div className={`text-xs ${expiring ? 'text-orange-500' : 'text-gray-400'}`}>
                                {age == null ? '—' : age === 0 ? 'today' : `${age}d`}
                                {expiring ? ' · expires soon' : ''}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(token.status)}`}>
                            {token.status?.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            {token.status !== 'added_to_recovery' && (
                              <button
                                onClick={() => handleAddToRecovery(token._id)}
                                className="text-green-600 hover:text-green-800 text-sm"
                                title="Add to Recovery"
                              >
                                ➕
                              </button>
                            )}
                            <a
                              href={`https://dexscreener.com/${token.chain}/${token.address}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 text-sm"
                              title="View on DEX Screener"
                            >
                              🔗
                            </a>
                            <button
                              onClick={() => handleDeleteToken(token._id)}
                              className="text-red-600 hover:text-red-800 text-sm"
                              title="Remove"
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {tokens.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    No tokens found. Click "Refresh All Sources" to fetch tokens.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Recovery Candidates Tab */}
          {activeTab === 'recovery' && (
            <div>
              <div className="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                <p className="text-yellow-800 dark:text-yellow-200">
                  <strong>Recovery Candidates:</strong> Tokens down 80%+ from ATH that may be good for recovery tracking.
                </p>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Token</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Current MC</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ATH MC</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Drop from ATH</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ATH Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {recoveryCandidates.map((token) => (
                      <tr key={token._id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-4 py-3">
                          <div className="flex items-center">
                            <span className="text-lg mr-2">{getChainEmoji(token.chain)}</span>
                            <div>
                              <div className="font-medium text-gray-900 dark:text-white">
                                {token.symbol}
                              </div>
                              <div className="text-xs text-gray-500">
                                {token.name}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-900 dark:text-white">
                          {formatPrice(token.currentMarketCap)}
                        </td>
                        <td className="px-4 py-3 text-gray-900 dark:text-white">
                          {formatPrice(token.athMarketCap)}
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-medium text-red-600">
                            {formatPercent(token.athDropPercent)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-500">
                          {formatDate(token.athDate)}
                        </td>
                        <td className="px-4 py-3">
                          {token.addedToRecovery || token.addedToTokens ? (
                            <span className="px-3 py-1 bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300 rounded text-sm inline-flex items-center">
                              ✅ Added
                            </span>
                          ) : (
                            <button
                              onClick={() => handleAddToRecovery(token._id)}
                              className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
                            >
                              Add to Recovery
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {recoveryCandidates.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    No recovery candidates found. Tokens down 80%+ from ATH will appear here.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="max-w-2xl">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <h3 className="text-lg font-bold mb-6 text-gray-900 dark:text-white">
                  Aggregator Settings
                </h3>
                
                <div className="space-y-6">
                  {/* Enable/Disable */}
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="font-medium text-gray-900 dark:text-white">Enable Aggregator</label>
                      <p className="text-sm text-gray-500">Turn on/off automatic token discovery</p>
                    </div>
                    <button
                      onClick={() => setSettings({ ...settings, enabled: !settings.enabled })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.enabled ? 'bg-blue-600' : 'bg-gray-300'}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>
                  
                  {/* Multi-Chain Support */}
                  <div>
                    <label className="block font-medium text-gray-900 dark:text-white mb-2">
                      Enabled Chains
                    </label>
                    <p className="text-sm text-gray-500 mb-3">Select which chains to aggregate tokens from</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {Object.entries(settings.chains || {}).map(([chainId, chainConfig]) => (
                        <div
                          key={chainId}
                          onClick={() => {
                            setSettings({
                              ...settings,
                              chains: {
                                ...settings.chains,
                                [chainId]: { ...chainConfig, enabled: !chainConfig.enabled }
                              }
                            });
                          }}
                          className={`flex items-center justify-between p-3 rounded-lg cursor-pointer border-2 transition-all ${
                            chainConfig.enabled 
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-lg">
                              {chainId === 'solana' && '☀️'}
                              {chainId === 'base' && '🔵'}
                              {chainId === 'bsc' && '🟡'}
                              {chainId === 'ethereum' && '💎'}
                              {chainId === 'arbitrum' && '🔷'}
                              {chainId === 'polygon' && '🟣'}
                            </span>
                            <span className="font-medium text-gray-900 dark:text-white">
                              {chainConfig.name}
                            </span>
                          </div>
                          <div className={`w-4 h-4 rounded-full ${chainConfig.enabled ? 'bg-blue-500' : 'bg-gray-300'}`}>
                            {chainConfig.enabled && (
                              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-400 mt-2">
                      Enabled: {Object.values(settings.chains || {}).filter(c => c.enabled).length} chain(s)
                    </p>
                  </div>
                  
                  {/* Market Cap Range */}
                  <div>
                    <label className="block font-medium text-gray-900 dark:text-white mb-2">
                      Market Cap Range ($)
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm text-gray-500">Min</label>
                        <input
                          type="number"
                          min={0}
                          value={settings.filters?.minMarketCap ?? 0}
                          onChange={(e) => setSettings({
                            ...settings,
                            filters: { ...settings.filters, minMarketCap: parseInt(e.target.value) || 0 }
                          })}
                          className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-gray-500">Max</label>
                        <input
                          type="number"
                          min={0}
                          value={settings.filters?.maxMarketCap || 500000}
                          onChange={(e) => setSettings({
                            ...settings,
                            filters: { ...settings.filters, maxMarketCap: parseInt(e.target.value) || 0 }
                          })}
                          className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                        />
                      </div>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      Only track tokens with market cap
                      {(settings.filters?.minMarketCap ?? 0) > 0
                        ? ` above $${(settings.filters.minMarketCap).toLocaleString()}`
                        : ''}
                      {' '}and below ${(settings.filters?.maxMarketCap || 500000).toLocaleString()}
                    </p>
                  </div>
                  
                  {/* 24h Change Range */}
                  <div>
                    <label className="block font-medium text-gray-900 dark:text-white mb-2">
                      24h Price Change Range (%)
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm text-gray-500">Min</label>
                        <input
                          type="number"
                          value={settings.filters?.minChange24h || -100}
                          onChange={(e) => setSettings({
                            ...settings,
                            filters: { ...settings.filters, minChange24h: parseInt(e.target.value) }
                          })}
                          className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-gray-500">Max</label>
                        <input
                          type="number"
                          value={settings.filters?.maxChange24h || -60}
                          onChange={(e) => setSettings({
                            ...settings,
                            filters: { ...settings.filters, maxChange24h: parseInt(e.target.value) }
                          })}
                          className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                        />
                      </div>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      Find tokens down between {settings.filters?.minChange24h || -100}% and {settings.filters?.maxChange24h || -60}% in 24h
                    </p>
                  </div>
                  
                  {/* ATH Drop Threshold */}
                  <div>
                    <label className="block font-medium text-gray-900 dark:text-white mb-2">
                      ATH Drop Threshold (%)
                    </label>
                    <input
                      type="number"
                      value={settings.filters?.athDropThreshold || -80}
                      onChange={(e) => setSettings({
                        ...settings,
                        filters: { ...settings.filters, athDropThreshold: parseInt(e.target.value) }
                      })}
                      className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      Auto-add to recovery if token is down more than {Math.abs(settings.filters?.athDropThreshold || 80)}% from ATH
                    </p>
                  </div>

                  {/* Dead-token Floor (max ATH drop) */}
                  <div>
                    <label className="block font-medium text-gray-900 dark:text-white mb-2">
                      Dead-Token Floor — Max ATH Drop (%)
                    </label>
                    <input
                      type="number"
                      value={settings.filters?.maxAthDrop ?? -95}
                      onChange={(e) => setSettings({
                        ...settings,
                        filters: { ...settings.filters, maxAthDrop: parseInt(e.target.value) }
                      })}
                      className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      Exclude effectively-dead tokens: skip recovery if down {Math.abs(settings.filters?.maxAthDrop ?? 95)}% or more from ATH (e.g. ~-100%)
                    </p>
                  </div>
                  
                  {/* Auto Add to Token Analytics */}
                  <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <div>
                      <label className="font-medium text-gray-900 dark:text-white">Auto-Add to Token Analytics</label>
                      <p className="text-sm text-gray-500">Add discovered tokens to main token tracking for monitoring</p>
                    </div>
                    <button
                      onClick={() => setSettings({ ...settings, autoAddToTokens: !settings.autoAddToTokens })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.autoAddToTokens ? 'bg-blue-600' : 'bg-gray-300'}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.autoAddToTokens ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>
                  
                  {/* Auto Add to Recovery */}
                  <div className="flex items-center justify-between p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <div>
                      <label className="font-medium text-gray-900 dark:text-white">Auto-Add to Recovery Tracking</label>
                      <p className="text-sm text-gray-500">Also mark as recovery if down {Math.abs(settings.filters?.athDropThreshold || 80)}%+ from ATH</p>
                    </div>
                    <button
                      onClick={() => setSettings({ ...settings, autoAddToRecovery: !settings.autoAddToRecovery })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.autoAddToRecovery ? 'bg-green-600' : 'bg-gray-300'}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.autoAddToRecovery ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>
                  
                  {/* Max Tokens Per Refresh */}
                  <div>
                    <label className="block font-medium text-gray-900 dark:text-white mb-2">
                      Max Tokens Per Refresh
                    </label>
                    <input
                      type="number"
                      value={settings.maxTokensPerRefresh || 50}
                      onChange={(e) => setSettings({ ...settings, maxTokensPerRefresh: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                    />
                    <p className="text-sm text-gray-500 mt-1">Limit tokens processed per refresh cycle</p>
                  </div>
                  
                  {/* Save Button */}
                  <div className="pt-4 border-t">
                    <button
                      onClick={handleSaveSettings}
                      disabled={savingSettings}
                      className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      {savingSettings ? 'Saving...' : 'Save Settings'}
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Safety Filters Section */}
              <div className="mt-6 bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <button
                  onClick={() => setShowSafetyFilters(!showSafetyFilters)}
                  className="w-full flex items-center justify-between text-left"
                >
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                      🛡️ Safety Filters (Anti-Honeypot)
                    </h3>
                    <p className="text-sm text-gray-500">Protect against scams, honeypots, and low-quality tokens</p>
                  </div>
                  <span className="text-xl">{showSafetyFilters ? '▼' : '▶'}</span>
                </button>
                
                {showSafetyFilters && (
                  <div className="mt-6 space-y-4">
                    {/* Liquidity */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Min Liquidity ($)
                        </label>
                        <input
                          type="number"
                          value={settings.safetyFilters?.minLiquidity || 0}
                          onChange={(e) => setSettings({
                            ...settings,
                            safetyFilters: { ...settings.safetyFilters, minLiquidity: parseInt(e.target.value) }
                          })}
                          className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Min Pair Age (hours)
                        </label>
                        <input
                          type="number"
                          value={settings.safetyFilters?.minPairAgeHours || 0}
                          onChange={(e) => setSettings({
                            ...settings,
                            safetyFilters: { ...settings.safetyFilters, minPairAgeHours: parseInt(e.target.value) }
                          })}
                          className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                        />
                      </div>
                    </div>

                    {/* Max Pair Age */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Max Pair Age (hours)
                        </label>
                        <input
                          type="number"
                          value={settings.safetyFilters?.maxPairAgeHours || 0}
                          onChange={(e) => setSettings({
                            ...settings,
                            safetyFilters: { ...settings.safetyFilters, maxPairAgeHours: parseInt(e.target.value) }
                          })}
                          className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          {settings.safetyFilters?.maxPairAgeHours
                            ? `Skip tokens older than ~${(settings.safetyFilters.maxPairAgeHours / 24).toFixed(0)} days`
                            : 'No max age limit'}
                        </p>
                      </div>
                    </div>
                    
                    {/* Volume & Transactions */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Min 24h Volume ($)
                        </label>
                        <input
                          type="number"
                          value={settings.safetyFilters?.min24hVolume || 0}
                          onChange={(e) => setSettings({
                            ...settings,
                            safetyFilters: { ...settings.safetyFilters, min24hVolume: parseInt(e.target.value) }
                          })}
                          className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Min 24h Transactions
                        </label>
                        <input
                          type="number"
                          value={settings.safetyFilters?.min24hTxns || 0}
                          onChange={(e) => setSettings({
                            ...settings,
                            safetyFilters: { ...settings.safetyFilters, min24hTxns: parseInt(e.target.value) }
                          })}
                          className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                        />
                      </div>
                    </div>
                    
                    {/* Honeypot Detection */}
                    <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                      <h4 className="font-medium text-red-800 dark:text-red-200 mb-3">🚫 Honeypot Detection</h4>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Min 24h Buys
                          </label>
                          <input
                            type="number"
                            value={settings.safetyFilters?.min24hBuys || 0}
                            onChange={(e) => setSettings({
                              ...settings,
                              safetyFilters: { ...settings.safetyFilters, min24hBuys: parseInt(e.target.value) }
                            })}
                            className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Min 24h Sells
                          </label>
                          <input
                            type="number"
                            value={settings.safetyFilters?.min24hSells || 0}
                            onChange={(e) => setSettings({
                              ...settings,
                              safetyFilters: { ...settings.safetyFilters, min24hSells: parseInt(e.target.value) }
                            })}
                            className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                          />
                          <p className="text-xs text-red-600 mt-1">Low sells = potential honeypot</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Max Buy/Sell Ratio
                          </label>
                          <input
                            type="number"
                            value={settings.safetyFilters?.maxBuySellRatio || 10}
                            onChange={(e) => setSettings({
                              ...settings,
                              safetyFilters: { ...settings.safetyFilters, maxBuySellRatio: parseInt(e.target.value) }
                            })}
                            className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                          />
                          <p className="text-xs text-red-600 mt-1">High ratio = can't sell</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Activity Checks */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Min 6h Transactions
                        </label>
                        <input
                          type="number"
                          value={settings.safetyFilters?.min6hTxns || 0}
                          onChange={(e) => setSettings({
                            ...settings,
                            safetyFilters: { ...settings.safetyFilters, min6hTxns: parseInt(e.target.value) }
                          })}
                          className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Min 1h Transactions
                        </label>
                        <input
                          type="number"
                          value={settings.safetyFilters?.min1hTxns || 0}
                          onChange={(e) => setSettings({
                            ...settings,
                            safetyFilters: { ...settings.safetyFilters, min1hTxns: parseInt(e.target.value) }
                          })}
                          className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                        />
                        <p className="text-xs text-gray-500 mt-1">Ensures token is actively traded</p>
                      </div>
                    </div>
                    
                    {/* Save Safety Filters */}
                    <div className="pt-4">
                      <button
                        onClick={handleSaveSettings}
                        disabled={savingSettings}
                        className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                      >
                        {savingSettings ? 'Saving...' : 'Save Safety Filters'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Info Box */}
              <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <p className="text-blue-800 dark:text-blue-200 text-sm">
                  <strong>How it works:</strong> Refresh scans enabled chains for tokens matching your filters.
                  Tokens that never reach recovery tracking are auto-removed after 7 days.
                  Successfully added recovery tokens are kept.
                </p>
              </div>
            </div>
          )}

          {/* Stats Tab */}
          {activeTab === 'stats' && stats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <div className="text-3xl font-bold text-blue-600">{stats.totalTokens}</div>
                <div className="text-gray-600 dark:text-gray-400">Total Tokens</div>
              </div>
              
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <div className="text-3xl font-bold text-yellow-600">{stats.recoveryCandidates}</div>
                <div className="text-gray-600 dark:text-gray-400">Recovery Candidates</div>
              </div>
              
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <div className="text-3xl font-bold text-green-600">{stats.addedToRecovery}</div>
                <div className="text-gray-600 dark:text-gray-400">Added to Recovery</div>
              </div>
              
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <div className="text-3xl font-bold text-purple-600">{stats.sources?.enabled || 0}</div>
                <div className="text-gray-600 dark:text-gray-400">Active Sources</div>
              </div>
              
              {/* Chain Distribution */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 md:col-span-2 lg:col-span-4">
                <h3 className="font-medium text-gray-900 dark:text-white mb-4">Tokens by Chain</h3>
                <div className="flex gap-6 flex-wrap">
                  {Object.entries(stats.byChain || {}).map(([chain, count]) => (
                    <div key={chain} className="flex items-center gap-2">
                      <span className="text-2xl">{getChainEmoji(chain)}</span>
                      <div>
                        <div className="font-bold text-lg">{count}</div>
                        <div className="text-sm text-gray-500 capitalize">{chain}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
